import { useState, useCallback, useEffect } from 'react';
import Cropper from 'react-easy-crop';
import { Button } from '@/components/ui/button';
import "./cropper.css";
import { Slider } from '@/components/ui/slider';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ZoomIn, ZoomOut, RotateCw, Check, X } from 'lucide-react';
import { t } from '@/i18n';

// Helper function to create cropped image
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

const getCroppedImg = async (imageSrc, pixelCrop, rotation = 0) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  const maxSize = Math.max(image.width, image.height);
  const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));

  canvas.width = safeArea;
  canvas.height = safeArea;

  ctx.translate(safeArea / 2, safeArea / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.translate(-safeArea / 2, -safeArea / 2);

  ctx.drawImage(
    image,
    safeArea / 2 - image.width * 0.5,
    safeArea / 2 - image.height * 0.5
  );

  const data = ctx.getImageData(0, 0, safeArea, safeArea);

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.putImageData(
    data,
    Math.round(0 - safeArea / 2 + image.width * 0.5 - pixelCrop.x),
    Math.round(0 - safeArea / 2 + image.height * 0.5 - pixelCrop.y)
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(blob);
    }, 'image/jpeg', 0.9);
  });
};

export default function ImageCropper({
  open,
  onClose,
  imageSrc,
  onCropComplete,
  aspectRatio = 1, // fixed ratio when allowAspectChange=false (avatar, cover)
  title = "Ritaglia immagine",
  cropShape = "rect", // "rect" or "round"
  allowAspectChange = false, // Instagram-style: Originale / 1:1 / 4:5 / Vert / Orizz
}) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [loading, setLoading] = useState(false);
  const [naturalRatio, setNaturalRatio] = useState(null);
  const [aspectKey, setAspectKey] = useState('original');

  // Detect the image's own aspect ratio → "Originale" shows the FULL photo, no forced crop
  useEffect(() => {
    if (!open || !imageSrc) return;
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setAspectKey('original');
    setNaturalRatio(null);
    createImage(imageSrc)
      .then((img) => setNaturalRatio(img.width / img.height))
      .catch(() => setNaturalRatio(null));
  }, [open, imageSrc]);

  const aspectOptions = [
    { key: 'original', label: t('cropOriginal'), value: naturalRatio || aspectRatio },
    { key: '1:1', label: '1:1', value: 1 },
    { key: '4:5', label: '4:5', value: 4 / 5 },
    { key: '9:16', label: t('cropVertical'), value: 9 / 16 },
    { key: '16:9', label: t('cropHorizontal'), value: 16 / 9 },
  ];

  const activeAspect = allowAspectChange
    ? (aspectOptions.find((a) => a.key === aspectKey)?.value || 1)
    : aspectRatio;

  const selectAspect = (key) => {
    setAspectKey(key);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const onCropChange = useCallback((crop) => {
    setCrop(crop);
  }, []);

  const onZoomChange = useCallback((zoom) => {
    setZoom(zoom);
  }, []);

  const onCropCompleteCallback = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      // "Originale" untouched → publish the FULL image, no crop at all
      if (allowAspectChange && aspectKey === 'original' && zoom === 1 && rotation === 0) {
        const originalBlob = await fetch(imageSrc).then((r) => r.blob());
        onCropComplete(originalBlob);
        onClose();
        return;
      }
      if (!croppedAreaPixels) return;
      const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels, rotation);
      onCropComplete(croppedImage);
      onClose();
    } catch (e) {
      console.error('Error cropping image:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleRotate = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="cr-shell z-[130]"
        aria-describedby={undefined}
        data-testid="image-cropper-dialog"
      >
        <DialogHeader className="cr-head">
          <DialogTitle className="cr-title">{title}</DialogTitle>
        </DialogHeader>

        {/* Crop area — pinch to zoom + drag supported natively */}
        <div className="cr-stage">
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={activeAspect}
              cropShape={cropShape}
              showGrid={true}
              objectFit="cover"
              onCropChange={onCropChange}
              onZoomChange={onZoomChange}
              onCropComplete={onCropCompleteCallback}
              style={{
                containerStyle: {
                  width: '100%',
                  height: '100%',
                  backgroundColor: '#000',
                },
                cropAreaStyle: {
                  border: '1.5px solid rgba(255,255,255,0.85)',
                  boxShadow: '0 0 0 9999px rgba(3, 2, 6, 0.78), 0 0 42px rgba(255,158,61,0.28)',
                },
              }}
            />
          )}
          <p className="cr-hint" data-testid="crop-pinch-hint">Pizzica per zoomare · trascina per inquadrare</p>
        </div>

        {/* Controls — compact, always above iOS home indicator / bottom nav */}
        <div
          className="cr-bar"
        >
          {/* Aspect ratio selector (Instagram-style) */}
          {allowAspectChange && (
            <div className="cr-chips scrollbar-hide" data-testid="aspect-ratio-selector">
              {aspectOptions.map((a) => (
                <button
                  key={a.key}
                  onClick={() => selectAspect(a.key)}
                  className="cr-chip"
                  data-on={aspectKey === a.key}
                  style={{ animationDelay: `${0.04 * aspectOptions.indexOf(a)}s` }}
                  data-testid={`aspect-${a.key}`}
                >
                  {a.label}
                </button>
              ))}
            </div>
          )}

          {/* Zoom slider + rotate on one row */}
          <div className="cr-row">
            <ZoomOut className="w-4 h-4 shrink-0" />
            <Slider
              value={[zoom]}
              min={1}
              max={3}
              step={0.05}
              onValueChange={([value]) => setZoom(value)}
              className="flex-1"
              data-testid="zoom-slider"
            />
            <ZoomIn className="w-4 h-4 shrink-0" />
            <button
              onClick={handleRotate}
              className="cr-rot"
              data-testid="rotate-btn"
            >
              <RotateCw className="w-4 h-4" />
            </button>
          </div>

          {/* Action buttons */}
          <div className="cr-actions">
            <Button
              variant="ghost"
              className="cr-ghost"
              onClick={onClose}
              data-testid="crop-cancel-btn"
            >
              <X className="w-4 h-4 mr-2" />
              Annulla
            </Button>
            <Button
              className="cr-cta"
              onClick={handleConfirm}
              disabled={loading}
              data-testid="crop-confirm-btn"
            >
              {loading ? (
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Conferma
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
